import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { api } from "../../../../api";
import {
  CloseIconComments,
  MaxamizeIcon,
  MinimizeIcon,
  ResponderCommentIcon,
  StudentCommentIcon,
  SendIcon,
} from "../../../core/customIcons";

import { styles } from "../../styles/detailsView/detailViewDrawer";
import { useTheme } from "@mui/material/styles";
import { LoadingIcon } from "../../loadingIcon.js";
import { WebSocketContext } from "../../../context/websocketsContext.js";

const DetailViewDrawer = ({ data, open, handleOpen }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [subscribe, unsubscribe] = useContext(WebSocketContext);

  const theme = useTheme();
  const style = styles(theme);

  // FEAT-054: the drawer now reads/writes the unified `messages` store. Map a message
  // ({messageId, senderRole, text, createdAt}) into the shape this view already renders
  // ({id, role:"user"|"student", comment, dateCreated}) so the styled markup is unchanged.
  const toComment = (m) => ({
    id: m.messageId,
    role: m.senderRole === "student" ? "student" : "user",
    comment: m.text,
    dateCreated: m.createdAt,
    clientMsgId: m.clientMsgId,
  });

  const fetchComments = async () => {
    const response = await api("get", { incidentId: data.id }, "messages");
    if (response.status === 200) {
      const list = response.data && response.data.data ? response.data.data
        : Array.isArray(response.data) ? response.data : [];
      const tempComments = list.map(toComment);
      tempComments.sort(
        (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated)
      );
      setComments(tempComments);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (comments.length > 0) {
      const element = document.getElementById("commentDiv");
      if (element) {
        element.scrollTop = element.scrollHeight;
      }
    }
    // eslint-disable-next-line
  }, [comments]);

  useEffect(() => {
    // Functional setState + de-dupe by id so re-subscribing on each render isn't needed and a
    // message can't be appended twice (fetch + live race).
    const appendUnique = (c) =>
      setComments((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, c]));
    subscribe("comments", (messageData) => {
      if (messageData.incidentId !== data.id) return;
      if (messageData.command === "message" && messageData.message) {
        appendUnique(toComment(messageData.message)); // FEAT-054 unified chat
      } else if (messageData.command === "newCommentStudent" && messageData.comment) {
        appendUnique(messageData.comment); // legacy path (e.g. an installed Flutter app)
      }
    });
    return () => {
      unsubscribe("comments");
    };
    // eslint-disable-next-line
  }, [data.id, subscribe, unsubscribe]);

  useEffect(() => {
    if (open === "max") {
      fetchComments();
    }
    // eslint-disable-next-line
  }, [open]);

  const handleComment = async () => {
    // Don't send empty/whitespace-only messages, and guard against double-submit.
    if (submitting || !newComment.trim()) {
      return;
    }
    setSubmitting(true);
    // FEAT-054: send via the unified message endpoint (append-only; no incident re-versioning).
    const messageBody = { incident: data.id, incidentId: data.id, text: newComment };
    const created = await api("post", messageBody, "message");
    if (created.status === 200 && created.data) {
      const mapped = toComment(created.data);
      setComments((prev) => (prev.some((x) => x.id === mapped.id) ? prev : [...prev, mapped]));
      setNewComment("");
    }
    setSubmitting(false);
  };

  // Enter sends the message; Shift+Enter inserts a newline (standard chat UX).
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleComment();
    }
  };

  return (
    <Container
      sx={[
        style.detailViewDrawerContainer,
        open === "min" || open === "max"
          ? style.detailViewDrawerContainerOpen
          : style.detailViewDrawerContainerClose,
        open === "min" && style.detailViewDrawerContainerMin,
        open === "max" && style.detailViewDrawerContainerMax,
      ]}
    >
      <Container sx={style.detailViewDrawerContainerHeader}>
        <Typography
          variant="h5"
          sx={style.detailViewDrawerContainerCommentsTitle}
        >
          Chat with Student
        </Typography>
        <Container sx={style.detailViewDrawerMinMaxContainer}>
          <MinimizeIcon
            onClick={() => handleOpen("min")}
            sx={[
              style.detailViewDrawerContainerHeaderIcon,
              open === "min" && style.hide,
            ]}
          />
          <MaxamizeIcon
            onClick={() => handleOpen("max")}
            sx={[
              style.detailViewDrawerContainerHeaderIcon,
              open === "max" && style.hide,
            ]}
          />
          <CloseIconComments
            onClick={() => handleOpen("closed")}
            sx={style.detailViewDrawerContainerHeaderIcon}
          />
        </Container>
      </Container>

      {loading ? (
        <Container sx={style.detailViewDrawerContainerComments}>
          <LoadingIcon page="comments" />
        </Container>
      ) : (
        <Container
          id="commentDiv"
          sx={
            open === "min"
              ? style.hide
              : style.detailViewDrawerContainerComments
          }
        >
          {comments.map((comment, index) => (
            <Container key={index}>
              {comment.role === "user" && (
                <Container sx={style.detailViewDrawerCommentContainer}>
                  <Container
                    sx={[
                      style.detailViewDrawerContainerComment,
                      style.commentResponder,
                    ]}
                  >
                    <Typography sx={style.detailViewDrawerContainerCommentText}>
                      {comment.comment}
                    </Typography>
                    <Typography
                      sx={[
                        style.detailViewDrawerContainerCommentDate,
                        style.responderDate,
                      ]}
                    >
                      {new Date(comment.dateCreated + "+0000").toLocaleString({
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        hour12: false,
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Container>
                  <ResponderCommentIcon sx={style.responderIcon} />
                </Container>
              )}
              {comment.role === "student" && (
                <Container sx={style.detailViewDrawerCommentContainer}>
                  <StudentCommentIcon sx={style.studentIcon} />
                  <Container
                    sx={[
                      style.detailViewDrawerContainerComment,
                      style.commentStudent,
                    ]}
                  >
                    <Typography sx={style.detailViewDrawerContainerCommentText}>
                      {comment.comment}
                    </Typography>
                    <Typography
                      sx={[
                        style.detailViewDrawerContainerCommentDate,
                        style.studentDate,
                      ]}
                    >
                      {new Date(comment.dateCreated + "+0000").toLocaleString({
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        hour12: false,
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Container>
                </Container>
              )}
            </Container>
          ))}
        </Container>
      )}
      <Container
        sx={open === "min" ? style.hide : style.detailViewDrawerContainerFooter}
      >
        <TextField
          multiline
          placeholder="Type something here..."
          sx={style.detailViewDrawerContainerFooterTextField}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          value={newComment}
          disabled={submitting}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {submitting ? (
                  <CircularProgress />
                ) : (
                  <SendIcon onClick={() => handleComment()}>Send</SendIcon>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Container>
    </Container>
  );
};

export default DetailViewDrawer;
